import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, EmptyState, money } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CurrencyDollar, TrendUp, Trophy, Truck } from "@phosphor-icons/react";

const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }).slice(0, 7);
const monthLabel = (m) => new Date(`${m}-15`).toLocaleDateString("en-US", { month: "long", year: "numeric" });

function Tiers({ amount }) {
  const { data } = useQuery({ queryKey: ["commission-tiers"], queryFn: async () => (await api.get("/commission/tiers")).data });
  if (!data) return null;
  return (
    <Panel className="p-5" data-testid="commission-tiers">
      <p className="font-heading font-bold text-slate-900 mb-3">Commission Structure</p>
      <div className="space-y-1">{data.tiers.map((t) => {
        const on = amount >= t.from && amount <= t.to + 0.99;
        return <div key={t.from} className={`flex justify-between text-sm px-3 py-1.5 rounded-lg ${on ? "bg-blue-600 text-white font-semibold" : "text-slate-600"}`}><span className="font-mono">{money(t.from)} – {money(t.to)}</span><span>{money(t.commission)}</span></div>;
      })}
        <div className={`flex justify-between text-sm px-3 py-1.5 rounded-lg ${amount > 100000 ? "bg-blue-600 text-white font-semibold" : "text-slate-600"}`}><span className="font-mono">Above {money(100001)}</span><span>+$50 / extra $10k</span></div>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Based on total load amount booked per calendar month (US Eastern). Effective from 1 November 2026.</p>
    </Panel>
  );
}

function Summary({ e }) {
  const nt = e.next_tier;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Panel className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 border-slate-800 text-white sm:col-span-1">
        <p className="text-sm text-white/60 flex items-center gap-1.5"><CurrencyDollar size={16} />Commission · {monthLabel(e.month)}</p>
        <p className="text-4xl font-heading font-extrabold mt-1" data-testid="commission-amount">{money(e.commission)}</p>
        <p className="text-[11px] text-white/50 mt-2" data-testid="commission-period">Counting loads booked {e.period_start} → {e.period_end}</p>
        {!e.active && <p className="text-xs text-amber-300 mt-1">Commission starts 1 November 2026</p>}
      </Panel>
      <Panel className="p-6"><p className="text-sm text-slate-500 flex items-center gap-1.5"><Truck size={16} />Load amount booked</p><p className="text-3xl font-heading font-extrabold text-slate-900 mt-1" data-testid="commission-volume">{money(e.load_amount)}</p><p className="text-xs text-slate-500">{e.loads_count} load{e.loads_count === 1 ? "" : "s"}</p></Panel>
      <Panel className="p-6"><p className="text-sm text-slate-500 flex items-center gap-1.5"><TrendUp size={16} />Next tier</p>
        {nt && <><p className="text-3xl font-heading font-extrabold text-emerald-600 mt-1" data-testid="commission-next">{money(nt.commission)}</p><p className="text-xs text-slate-500">Book {money(nt.remaining)} more to reach {money(nt.at)}</p></>}
      </Panel>
    </div>
  );
}

function LoadsTable({ loads }) {
  if (!loads?.length) return <EmptyState icon={Truck} title="No loads booked this month" hint="Loads you book count towards your monthly commission." />;
  return (
    <div className="divide-y divide-slate-100">{loads.map((l) => (
      <div key={l.id} className="flex justify-between gap-3 px-5 py-2.5 text-sm" data-testid={`commission-load-${l.id}`}>
        <span><span className="font-mono font-semibold text-slate-900">{l.reference}</span> <span className="text-slate-500">· {l.origin || "—"} → {l.destination || "—"}</span><span className="block text-[11px] text-slate-400" data-testid={`commission-load-date-${l.id}`}>Booked {l.booked_date}</span></span>
        <span className="font-semibold text-slate-900">{money(l.rate)}</span>
      </div>))}</div>
  );
}

function MyEarnings({ month }) {
  const { data } = useQuery({ queryKey: ["commission-me", month], queryFn: async () => (await api.get("/commission/me", { params: { month } })).data });
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-5">
      <Summary e={data} />
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start">
        <Panel className="overflow-hidden"><p className="px-5 py-3 border-b border-slate-100 font-heading font-bold text-slate-900">Loads booked</p><LoadsTable loads={data.loads} /></Panel>
        <Tiers amount={data.load_amount} />
      </div>
    </div>
  );
}

function TeamEarnings({ month }) {
  const [pick, setPick] = useState(null);
  const { data } = useQuery({ queryKey: ["commission-team", month], queryFn: async () => (await api.get("/commission/team", { params: { month } })).data });
  const { data: one } = useQuery({ queryKey: ["commission-one", pick, month], enabled: !!pick, queryFn: async () => (await api.get(`/commission/team/${pick}`, { params: { month } })).data });
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  const rows = [...data.dispatchers].sort((a, b) => b.load_amount - a.load_amount);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start">
      <Panel className="overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex justify-between"><p className="font-heading font-bold text-slate-900">Dispatchers</p><p className="text-sm text-slate-500">Total payout <span className="font-bold text-slate-900" data-testid="team-commission-total">{money(data.total_commission)}</span></p></div>
        {!rows.length ? <EmptyState icon={Trophy} title="No dispatchers yet" hint="Add dispatchers in Team (Users)." /> : rows.map((r, i) => (
          <button key={r.id} onClick={() => setPick(r.id)} className="w-full grid grid-cols-[32px_1fr_auto_auto] gap-4 items-center px-5 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100" data-testid={`team-commission-${r.id}`}>
            <span className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold ${i === 0 && r.load_amount > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
            <span><span className="font-medium text-slate-900 block">{r.name}</span><span className="text-xs text-slate-500">{r.loads_count} load{r.loads_count === 1 ? "" : "s"} · {money(r.load_amount)}</span></span>
            <span className="text-xs text-slate-500 hidden sm:block">{r.next_tier ? `${money(r.next_tier.remaining)} to next` : ""}</span>
            <span className="font-heading font-extrabold text-slate-900">{money(r.commission)}</span>
          </button>))}
      </Panel>
      <Tiers amount={-1} />
      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="team-commission-detail">
          <DialogHeader><DialogTitle className="font-heading">{one?.name || "Dispatcher"} · {monthLabel(month)}</DialogTitle><DialogDescription>Earnings breakdown and loads booked.</DialogDescription></DialogHeader>
          {one && <div className="space-y-4"><Summary e={one} /><Panel className="overflow-hidden"><LoadsTable loads={one.loads} /></Panel></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Earnings() {
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const admin = user?.role === "admin";
  return (
    <div>
      <PageHeader title={admin ? "Dispatcher Earnings" : "My Earnings"} subtitle={admin ? "Monthly commission for every dispatcher, based on the total load amount they book" : "Your monthly commission grows with the total load amount you book"}
        actions={<Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="bg-white border-slate-300 w-44" data-testid="earnings-month" />} />
      {admin ? <TeamEarnings month={month} /> : <MyEarnings month={month} />}
    </div>
  );
}
